/*------------------------------------------------------------------------------
   About      : Contract types for the NBI Insights screen (epic DA-697, II-FE-1)

                Derived from `docs/nbi-insights-api-contract.md` in bidgely-quill.
                **quill is authoritative** — change that file and this one in the
                same PR, never one alone. Do not re-derive these shapes from
                quill's or proxypulse's source.

                The UI label is "NBI Insights"; identifiers stay
                `interesting-insight` because pingpong has an unrelated
                `NBIInsightDefinition` class.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { CONTENT_EDIT_ACTION,
         CONTENT_EDIT_STATUS,
         CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'
import { USER_ROLE }                   from 'framework/session/PermissionsManager'

/*------------------------------------------------------------------------------
   Catalog — GET /api/insight/getInsightCatalog

   Drives every dropdown, chip and value control. Read-only, Redis-cached 300s,
   relayed verbatim by proxypulse. Never reshape it client-side: the FE and BE
   would then disagree about what is allowed.
------------------------------------------------------------------------------*/

/** One row of quill's `derived-quantity-variable` table (DA-2366). */
export interface DerivedQuantityVariable {
  /** The pingpong `DerivedQuantity` enum constant. The insertable token is
   *  computed as `${CODE}` — it is never a field. */
  code                     : string
  /** The only thing a user may ever see. Never render `code`. */
  displayName              : string
  description             ?: string | null
  unit                    ?: string | null
  /**
   * Which of pingpong's four value sources the variable reads from. **Only
   * `BENCHMARK` varies by insight type** — it is the only group produced by the
   * per-type benchmark computer. `DEFINITION` values are authored metadata,
   * `DURATION` values are built before any computer runs, and the `HYBRID`
   * `APPLIANCE` falls back to the definition's own `applianceId`.
   */
  source                  ?: 'BENCHMARK' | 'DEFINITION' | 'DURATION' | 'HYBRID' | null
  /**
   * Insight types this variable resolves for — filter chips/dropdowns by it.
   *
   * ⚠️ `null` means **"this question does not apply"**, i.e. unrestricted. It is
   * NOT "unknown" and NOT "every type is listed": 7 of the 15 seeded rows are
   * legitimately null, because only `BENCHMARK` rows are type-gated at all.
   * Never call `.includes()` on it without checking it is an array first.
   */
  allowedInsightTypes      : string[] | null
  /**
   * `DEFINITION` rows only. The insight field that must be SET ON THE RECORD for
   * this variable to resolve — `${PICK_UNIT_TYPE}` needs `pickUnitType`. This is
   * a per-record rule no type-level matrix can express: the variable is legal on
   * a `PEER` insight that sets the field and broken on a `SELF_COMPARE_WITH` one
   * that does not. quill enforces it on write.
   */
  requiresDefinitionField ?: string | null
  usableInTemplateText     : boolean
  usableInTemplateCriteria : boolean
  /** When true the token is invalid bare and MUST carry a format suffix —
   *  `${BILL_END_MONTH_NAME}`, never `${BILL_END}`. See `supportedDateFormats`. */
  dateFormatSuffix         : boolean
  /** The `DateFormat` constants allowed as a suffix, or null when not applicable. */
  supportedDateFormats    ?: string[] | null
  pluralizationRule       ?: string | null
  appliesWhen             ?: unknown | null
  /** Excluded from pickers; still rendered when already present on a record. */
  deprecated               : boolean
  /** Ascending display order for chips and dropdowns. */
  sortOrder                : number
}

/** Which classification fields a given insight type requires. Drives field
 *  visibility and requiredness on the form — do not hardcode per-type rules. */
export interface InsightTypeRequires {
  pickUnit    : boolean
  compareUnit : boolean
  gap         : boolean
  direction   : boolean
}

/** Static map from quill's `ii-interfaces.ts`. */
export interface InsightTypeDef {
  code                : string
  displayName         : string
  /** Code-derivation prefix fallback when the group has none. */
  shortCode           : string
  requires            : InsightTypeRequires
  benchmarkPercentile : boolean
}

/** Static map from quill's `ii-interfaces.ts`.
 *
 *  ⚠️ `shortCode` is null for `SEASON_COMPARISON` and `SOLAR_GENERATION` — no
 *  pingpong definition establishes a prefix for them, so `buildCode` refuses
 *  rather than guessing (a wrong prefix writes a wrong Cassandra clustering
 *  key). Those need `codeOverride`, which is FULL_ACCESS-only. */
export interface InsightGroupDef {
  code        : string
  displayName : string
  shortCode   : string | null
}

/** Grammar for `templateCriteria` expressions. The operator list and the
 *  validation pattern both come from here — never hardcode either. */
export interface CriteriaGrammar {
  operators     : string[]
  /** Placeholder repeated as the left-hand side of a compound expression. */
  compoundToken : string
  /** Anchored regex source. `compoundToken` appears in it literally, so
   *  substitute it before compiling. At most one `&&` per the pattern. */
  pattern       : string
}

/** An appliance option, resolved for display by `applianceId`. */
export interface InsightApplianceOption {
  applianceId : number
  displayName : string
  category   ?: string | null
}

export interface InsightApplianceCategoryOption {
  code        : string
  displayName : string
}

export interface InsightCatalog {
  derivedQuantities    : DerivedQuantityVariable[]
  types                : InsightTypeDef[]
  groups               : InsightGroupDef[]
  criteriaGrammar      : CriteriaGrammar
  appliances           : InsightApplianceOption[]
  applianceCategories  : InsightApplianceCategoryOption[]
}

/*------------------------------------------------------------------------------
   Workflow — GET /api/insight/getReviewWorkflow

   Relayed verbatim. Extends the shared QA_WORKFLOW shape (recos, surveys) with
   `statusLabels`, which is where display text lives: `READY_FOR_QA` renders as
   "Ready for review" while the stored value is unchanged.
------------------------------------------------------------------------------*/

export interface InsightReviewWorkflowEntry {
  userRoles       : USER_ROLE[]
  qaWorkflowEdges : {
    from        : CONTENT_VERIFICATION_STATUS
    to          : CONTENT_VERIFICATION_STATUS
    conditions ?: unknown
  }[]
  actions         : Record<CONTENT_VERIFICATION_STATUS, CONTENT_EDIT_ACTION[]>
  /** Stored status value → text to show the user. Never hardcode a status label. */
  statusLabels    : Partial<Record<CONTENT_VERIFICATION_STATUS, string>>
}

export type InsightReviewWorkflow = InsightReviewWorkflowEntry[]

/*------------------------------------------------------------------------------
   The insight record
------------------------------------------------------------------------------*/

/** One edge of the QA-status dropdown, computed per record by quill (DA-2372).
 *
 *  A blocked edge arrives WITH a reason rather than being omitted, so the UI
 *  greys it out and explains instead of silently hiding a transition the user
 *  expects. Render `blockedReason` verbatim; the gate is a monotonic floor
 *  ("has reached", not "is in") and cannot be recomputed client-side. */
export interface AllowedTransition {
  to             : CONTENT_VERIFICATION_STATUS
  /** Present ⇒ the edge is disabled. Absent ⇒ selectable. */
  blockedReason ?: string
}

export interface InsightGroupInfo {
  group   : string
  weight ?: number | null
}

/** Derived-quantity code → expression string, e.g. `{ USAGE_DELTA_PERCENTAGE: '>=10' }`.
 *  Merged server-side, never replaced — always send the whole object. */
export type TemplateCriteria = Record<string, string>

/** Per-locale template text. ⚠️ Only the default locale is writable today:
 *  `updateAll` has no equivalent of the reco path's `updateLocalisedContent`,
 *  so a non-`en` edit is silently dropped (open on DA-2369). */
export type LocalizedText = Record<string, string>

/** quill's `VALIDATION_STATUS` — exactly two values, no WARN and no UNKNOWN.
 *  "Never checked" is expressed by the slot being **absent**, not by a status. */
export type ValidationSlotStatus = 'PASS' | 'FAIL'

/** One recorded check. Mirrors quill's `ValidationSlot`. */
export interface ValidationCheckState {
  status     : ValidationSlotStatus
  checkedAt  : string
  /**
   * Free-form evidence, and **not always a string** — quill: "for `variables`
   * this is the validator's message list; for `semantic` it is the model's
   * verdict object verbatim, e.g. `{ issues, model, reasoning, verdict }`".
   *
   * Rendering it directly would print `[object Object]`; go through
   * `describeCheckDetail` in `insightValidation.ts`.
   */
  detail    ?: unknown
}

/**
 * The three recorded checks. Mirrors quill's `ValidationState`.
 *
 * ⚠️ **Server-owned — never send this.** quill lists it in
 * `SERVER_OWNED_INSIGHT_FIELDS` and *rejects* a client-supplied value rather
 * than ignoring it, because "a client that can write validationState can
 * self-certify its own content as validated — including the semantic slot,
 * which is supposed to be evidence that a model actually looked at the text".
 *
 * What quill does write, unprompted:
 * - **create** → `variables: PASS` (the deterministic pass ran before persisting,
 *   so a FAIL would have been returned as an error instead of stored).
 * - **update** → re-stamps `variables: PASS`, and **deletes** `semantic` and
 *   `capping` when `templateString` or `type` changed. Expiry drops the slot
 *   rather than marking it FAIL: "not checked" is the truth, and FAIL would read
 *   as a real rejection.
 *
 * An **absent slot means "never checked"** and must render neutral — that is
 * deliberately distinct from a recorded FAIL.
 *
 * `capping` has no writer yet (quill: "Not yet defined — see DA-2363") and
 * `semantic` is written by a phase-5 endpoint that does not exist yet, so both
 * are absent in practice today and the FE derives what it can. See
 * `deriveInsightValidation`.
 */
export interface InsightValidationState {
  variables ?: ValidationCheckState
  semantic  ?: ValidationCheckState
  capping   ?: ValidationCheckState
}

/** A tagged recommendation as returned alongside an insight. */
export interface TaggedRecommendation {
  documentId         : string
  recommendationId   : string
  title             ?: string
  editStatus        ?: CONTENT_EDIT_STATUS
  verificationStatus?: CONTENT_VERIFICATION_STATUS
  /** Recommendations use `disabled` too — see the note on `InsightRecord`. */
  disabled          ?: boolean
}

export interface InsightRecord {
  documentId          : string

  /* --- server-owned, read-only for life. Never send these back. --- */
  /** Derived once on create. Unique PER PILOT, not globally — never dedupe on
   *  it and never use it as a client-side map key. */
  code                : string
  /** `'II' + code.replace(/-/g, '')`. Immutable forever: pingpong re-renders
   *  stored insights from it on a locale switch. */
  insightId           : string
  /** Emitted as `= insightId`; never authored. */
  templateId         ?: string
  /** Auto-demotes PUBLISHED → MODIFIED on edit. */
  editStatus          : CONTENT_EDIT_STATUS

  /* --- workflow --- */
  verificationStatus  : CONTENT_VERIFICATION_STATUS
  /** Drive the QA-status dropdown from this, never from the full enum. */
  allowedTransitions  : AllowedTransition[]

  /* --- classification --- */
  type                : string
  validityType       ?: string | null
  timeOfUse          ?: string | null
  applianceId        ?: number | null
  measurementType    ?: string | null
  direction          ?: string | null
  sentimentType      ?: string | null
  /** A plain string on the record — NOT derived from the appliance catalog.
   *  The list column that resolved it via `appliances[].category` was reading
   *  the wrong source entirely. */
  category           ?: string | null
  groupInfo          ?: InsightGroupInfo | null

  /* --- type-conditional, gated by InsightTypeRequires --- */
  pickUnitType       ?: string | null
  pickUnitCount      ?: number | null
  compareUnitType    ?: string | null
  compareUnitCount   ?: number | null
  gap                ?: number | null
  benchmarkPercentile?: number | null

  /* --- content --- */
  /** Tokens are `${DERIVED_QUANTITY}` — dollar-brace, uppercase, exact enum
   *  constant. A `{{value}}`-style token is never substituted by pingpong and
   *  ships literally to end users. */
  templateString     ?: string | null
  templateExample    ?: string | null
  alternativeVersions?: string[] | null
  templateCriteria   ?: TemplateCriteria | null
  excludeCriteria    ?: TemplateCriteria | null
  link               ?: string | null

  /* --- relations and state --- */
  recommendations    ?: TaggedRecommendation[] | null
  /** Server-owned; see the type. Never include it in an update payload. */
  validationState    ?: InsightValidationState | null
  /** Server-owned. Stamped whenever `validationState` is written. */
  lastValidation     ?: string | null
  /**
   * ⚠️ The field is `disabled`, not `enabled` — quill's schema has no `enabled`
   * attribute at all, and it defaults to `false`.
   *
   * This was typed as `enabled` here, which meant the list's Active column read
   * `row.enabled !== false` → `undefined !== false` → **every insight always
   * rendered as Active**, and a write would have sent a field quill does not
   * know. Read `disabled === true` to mean inactive; absent means active.
   */
  disabled           ?: boolean
  locale             ?: string
  publishedAt        ?: string | null
  createdAt          ?: string
  updatedAt          ?: string
}

/*------------------------------------------------------------------------------
   Requests and responses
------------------------------------------------------------------------------*/

/** Per-item failure: `{ "<code>": "message" }`. One bad row does not fail the
 *  batch, so surface a partial-success summary rather than a single toast. */
export type InsightItemError = Record<string, string>

/** `GET /api/insight/getInsights?utilityDocumentId=…`
 *
 *  ⚠️ No `page`, `pageSize`, `sort` or `search` params — this returns the whole
 *  pilot, partitioned the same way `fetchRecos` partitions recommendations.
 *  Merge the three arrays and page/sort/filter locally. */
export interface GetInsightsResponse {
  draft     : InsightRecord[]
  modified  : InsightRecord[]
  published : InsightRecord[]
}

/** Fields a user may author on create. `code`, `insightId` and `editStatus` are
 *  absent by construction — quill rejects a client-supplied value rather than
 *  ignoring it. */
export type InsightCreateData = Partial<
  Pick<InsightRecord,
    'type' | 'validityType' | 'timeOfUse' | 'applianceId' | 'measurementType' |
    'direction' | 'sentimentType' | 'groupInfo' | 'pickUnitType' | 'pickUnitCount' |
    'compareUnitType' | 'compareUnitCount' | 'gap' | 'benchmarkPercentile' |
    'templateString' | 'templateExample' | 'alternativeVersions' |
    'templateCriteria' | 'excludeCriteria' | 'link' | 'disabled' | 'category' |
    'validationState' | 'lastValidation'>
>

export interface CreateInsightRequest {
  utilityDocumentId : string
  locale           ?: string
  /** FULL_ACCESS-only; rejected otherwise. Needed by the families whose group
   *  has no established prefix, and by the two whose code shape the rule cannot
   *  produce (`HIGHEST_LOWEST_DAY_OF_WEEK`, `APPLIANCE_CONSUMPTION_IN_A_DURATION`). */
  codeOverride     ?: string | null
  data              : InsightCreateData
}

export interface CreateInsightResponse {
  insight ?: InsightRecord
  error   ?: InsightItemError[]
}

/** One sparse patch. Carries ONLY changed fields.
 *
 *  `code`, `insightId` and `editStatus` are absent by construction, because
 *  `InsightCreateData` does not include them — the server owns all three and
 *  rejects a client-supplied value. */
export type InsightUpdatePatch = InsightCreateData & {
  documentId          : string
  /** Client-settable, unlike the three server-owned fields — the reco and
   *  survey paths send it the same way, and there is no dedicated transition
   *  route. Deliberately NOT part of `InsightCreateData`, so the form can never
   *  round-trip a stale status: only the explicit QA-status action sets it. */
  verificationStatus ?: CONTENT_VERIFICATION_STATUS
}

export interface UpdateAllRequest {
  pilotId : string
  data    : InsightUpdatePatch[]
}

export interface UpdateAllResponse {
  insights ?: InsightRecord[]
  error    ?: InsightItemError[]
}

/** Shared body of publishAll / unpublishAll / discardAllDrafts. */
export interface InsightBatchRequest {
  pilotId : string
  data    : string[]
}

export interface InsightBatchResponse {
  insights ?: InsightRecord[]
  results  ?: unknown[]
  error    ?: InsightItemError[]
}

export type TagRecommendationsMode = 'SET' | 'ADD' | 'REMOVE'

export interface TagRecommendationsRequest {
  insightDocumentId        : string
  recommendationDocumentIds: string[]
  /** Defaults to `SET` server-side. */
  mode                    ?: TagRecommendationsMode
}

/** Carries a FRESH `allowedTransitions` on the returned insight — re-render the
 *  QA-status dropdown from it rather than keeping the pre-tag array. */
export interface TagRecommendationsResponse {
  insight ?: InsightRecord
  error   ?: InsightItemError[]
}

/*------------------------------------------------------------------------------
   GenAI + template validation — DA-2401

   These three are proxypulse-native, NOT quill relays (except
   validate-template-text, which relays quill's stateless check, contract §2b).
   Shapes verified against proxypulse `routes/v1/insights_ai_service.py`.
------------------------------------------------------------------------------*/

/** One `${…}` token found in the text, resolved against the live registry. */
export interface TemplatePlaceholder {
  raw         : string
  /** null ⇒ not a known DerivedQuantity; the token would ship literally. */
  code        : string | null
  dateFormat  : string | null
  /**
   * The record field this token needs set, when it reads one.
   *
   * The endpoint is **stateless** — it receives `{ templateString, type }` and no
   * insight — so it cannot check this itself and does not try. It reports the
   * dependency here for a caller that DOES hold the record. `createInsight` /
   * `updateAll` enforce it on write, so ignoring it means finding out at save.
   */
  requiresDefinitionField ?: string | null
}

/** `POST /v1/insights/validate-template-text` → quill's `{ data: … }` unwrapped. */
export interface TemplateTextValidation {
  valid        : boolean
  errors       : string[]
  placeholders : TemplatePlaceholder[]
}

export interface GenerateAlternativeRequest {
  templateString          : string
  tone                   ?: string
  readingLevel           ?: string
  additionalInstructions ?: string
  /** Gates the per-type resolvability check on generated candidates. */
  type                   ?: string | null
  /** Clamped server-side to 1..3. */
  count                  ?: number
  /** Optional; only attributes the audit row. The insight may be unsaved. */
  pilotId                ?: string
  insightDocumentId      ?: string
}

/** A candidate rewrite. Invalid ones are returned FLAGGED rather than dropped,
 *  so the author sees why a suggestion was rejected instead of an empty modal.
 *  Validation fails closed: if quill is unreachable the candidate arrives
 *  `valid: false` with the reason, never unchecked-but-presented-as-fine. */
export interface InsightAlternative {
  text             : string
  valid            : boolean
  validationErrors : string[]
}

export interface GenerateAlternativeResponse {
  alternatives : InsightAlternative[]
  model        : string
}

/** Advisory only — never gates a save. Mechanical validation (DA-2368) remains
 *  the only content gate. */
export type SemanticVerdict = 'PASS' | 'WARN' | 'FAIL'

export interface SemanticValidateResponse {
  verdict   : SemanticVerdict
  reasoning : string
  issues    : string[]
  model     : string
}

/** The only fields forwarded to the model — proxypulse filters to this set, so
 *  an insight payload cannot leak arbitrary keys into a prompt. Mirrored here so
 *  the client sends exactly what will be used. */
export const SEMANTIC_CLASSIFICATION_FIELDS = [
  'type', 'sentimentType', 'direction', 'validityType', 'timeOfUse',
  'measurementType', 'applianceId',
] as const
